import { ConsentSettings } from "../consentSettings";
import { ContentInteraction } from "../interfaces";
import {
  AnalyticsProvider,
  ConversionEventDetail
} from "./analyticsProviderBase";
import hash from "object-hash";

export interface NativeEvent {
  event_timestamp_ms: number;
  tab_id: string;
  event_type: string;
  payload: {
    [key: string]: unknown;
  };
}

export class NativeAnalytics extends AnalyticsProvider {
  tab_id;

  constructor() {
    super();
    this.name = "NativeAnalytics";
  }

  object_hash(val: unknown) {
    return hash.sha1(val);
  }

  start(_consent_settings: ConsentSettings): void {
    this.has_consent_to_run = _consent_settings.performance == true;

    if (!this.has_consent_to_run) {
      this.log("NativeAnalytics does not have permission to start");
      this.has_started = true;
      return;
    } else if (!this.has_started) {
      this.tab_id = window.sessionStorage.tab_id || sv.generate_auid();
      window.sessionStorage.tab_id = this.tab_id;
      this.log("NativeAnalytics is starting...");

      // this is a hack because npm check does not recognise some valid types in the Navigator object
      // Unable to resolve in a elegant way at this time :-(
      // this.log("NativeAnalytics calculating userAgentData...");
      let user_agent_data;
      try {
        window.navigator["userAgentData"]
          .getHighEntropyValues([
            "architecture",
            "bitness",
            "model",
            "platformVersion",
            "fullVersionList"
          ])
          .then((found_user_agent_data) => {
            if (found_user_agent_data) {
              user_agent_data = found_user_agent_data;
            }
          });
      } catch (e) {
        // expected sometimes.
      }

      wspr.S(
        "ecommerce_error",
        `analytics_provider_${this.name}`,
        async (wspr_event) => {
          this.error_triggered(wspr_event);
          return true;
        }
      );

      wspr.S(
        "ecommerce_api_perf",
        `analytics_provider_${this.name}`,
        async (wspr_event) => {
          this.api_perf_triggered(wspr_event);
          return true;
        }
      );

      // this.log("NativeAnalytics calculating pdfViewerEnabled...");
      let pdf_viewer_enabled;
      try {
        pdf_viewer_enabled = window.navigator["pdfViewerEnabled"];
      } catch (e) {
        // expected sometimes.
      }

      // this.log("NativeAnalytics calculating memory_gb...");
      let memory_gb;
      try {
        memory_gb = window.navigator["deviceMemory"];
      } catch (e) {
        // expected sometimes.
      }

      // this.log("NativeAnalytics calculating visit data...");
      const visit_data = {
        event_timestamp_ms: 0,
        tab_id: this.tab_id,
        event_type: "visit",
        payload: {
          visitor_id: this.get_visitor_id(),
          user_agent: window.navigator?.userAgent,
          user_agent_data: {
            architecture: user_agent_data?.architecture,
            bitness: user_agent_data?.bitness,
            brands: user_agent_data?.brands,
            full_version_list: user_agent_data?.fullVersionList,
            mobile: user_agent_data?.mobile,
            model: user_agent_data?.model,
            platform: user_agent_data?.platform,
            platform_version: user_agent_data?.platformVersion
          },
          languages: window.navigator?.languages || [
            window.navigator?.language
          ],
          hardware: {
            cpu_cores: window.navigator?.hardwareConcurrency,
            memory_gb: memory_gb
          },
          platform: window.navigator?.platform,
          cookies_enabled: window.navigator?.cookieEnabled,
          pdf_viewer_enabled: pdf_viewer_enabled,
          java_enabled: window.navigator?.javaEnabled()
        }
      };

      // this.log("NativeAnalytics starting compare...");
      const found_visit_data = window.localStorage.getItem("visit_data");
      // this.log(this.object_hash(visit_data) + " != " + found_visit_data);
      if (this.object_hash(visit_data) != found_visit_data) {
        window.localStorage.setItem("visit_data", this.object_hash(visit_data));

        visit_data.event_timestamp_ms = this.get_timestamp();
        this.send_event(visit_data).then((response) => {
          if (response == "OK") {
            this.log("NativeAnalytics visit success");
          } else {
            console.error("NativeAnalytics visit failed");
          }
        });
      } else {
        this.log("NativeAnalytics visit already logged.");
      }

      this.has_started = true;
    }

    return;
  }

  get_visitor_id(): string {
    return sv.get_auid();
  }

  get_timestamp() {
    if (!Date.now) {
      Date.now = () => new Date().getTime();
    }
    return Date.now();
  }

  conversion_triggered(_conversion_event_detail: ConversionEventDetail): void {
    if (!this.has_consent_to_run) {
      return;
    } else {
      this.log_conversion();
    }

    return;
  }

  interaction_triggered(_data_content_attribs: ContentInteraction): void {
    if (!this.has_consent_to_run) {
      return;
    } else {
      this.log_interaction();
    }
  }

  async send_event(event: NativeEvent): Promise<unknown> {
    async function send(event): Promise<string> {
      let api_path =
        "https://qa-api2.digital.silversea.com/nativeAnalyticsSubmission";
      if (
        window.location.href.startsWith("https://www.silversea.com") ||
        window.location.href.startsWith("https://discover.silversea.com") ||
        window.location.href.startsWith("https://quote.silversea.com") ||
        window.location.href.startsWith("https://my.silversea.com") ||
        window.location.href.startsWith("https://booking.silversea.com")
      ) {
        api_path =
          "https://api2.digital.silversea.com/nativeAnalyticsSubmission";
      }

      const response = await fetch(api_path, {
        method: "post",
        body: JSON.stringify(event),
        headers: { "Content-Type": "application/json" }
      });

      const responseData = await response.text();

      if (response.ok) {
        console.log(
          `Native Event sent successfully: ${response?.status} ${responseData}`
        );
        return responseData;
      } else {
        throw new Error(
          `Native Event HTTP Error Response: ${response?.status} ${responseData}`
        );
      }
    }

    return this.retry_func(send, event, '"Send Native Event"', 3000, 5);
  }

  pageload_triggered(): void {
    if (!this.has_consent_to_run) {
      return;
    } else {
      this.log_pageload();
    }

    this.initialise_subscribers();

    const vd = sv.get_voyage_details();

    const currency = vd.voyage_price_currency || vd.lead_value_currency;

    const vp = window.visualViewport;

    this.send_event({
      event_timestamp_ms: this.get_timestamp(),
      tab_id: this.tab_id,
      event_type: "pageload",
      payload: {
        visitor_id: this.get_visitor_id(),
        href: window.location.href,
        referrer: document?.referrer,
        title: document?.title,
        performance: {
          timing: performance?.timing.toJSON(),
          navigation: performance?.getEntriesByType("navigation")[0],
          first_paint: performance?.getEntriesByName("first-paint")[0],
          first_contentful_paint: performance?.getEntriesByName(
            "first-contentful-paint"
          )[0],
          on_initial_render: performance?.getEntriesByName(
            "onInitialClientRender"
          )[0]
        },
        viewport: {
          height: vp?.height,
          width: vp?.width,
          scale: vp?.scale
        },
        custom: {
          page_type: sv.get_page_type(),
          page_cat1: sv.get_page_cat1(),
          page_cat2: sv.get_page_cat2(),
          platform_market: sv.get_platform_market(),
          user_country: sv.get_user_country(),
          user_language: sv.get_user_language(),
          voyage_adults_number: vd.adults_number,
          voyage_flight_option: vd.flight_option,
          voyage_kids_number: vd.kids_number,
          voyage_lead_value: vd.lead_value,
          voyage_lead_value_currency: vd.lead_value_currency,
          voyage_name: vd.name,
          voyage_arrival_date: vd.arrival_date,
          voyage_arrival_port_code: vd.arrival_port,
          voyage_code: vd.code,
          voyage_days_duration: vd.days_duration,
          voyage_departure_date: vd.departure_date,
          voyage_departure_port_code: vd.departure_port,
          voyage_price: vd.price,
          voyage_ship_name: vd.ship_name,
          voyage_suite: vd.suite,
          voyage_type: vd.type,
          version: sv.get_version(),
          currency: currency
        }
      }
    }).then((response) => {
      if (response == "OK") {
        this.log("NativeAnalytics pageload success");
      } else {
        console.error("NativeAnalytics pageload failed");
      }
    });
  }

  error_triggered(error_data): void {
    if (!this.has_consent_to_run) {
      return;
    } else {
      console.error("Native Analytics Error Triggered", error_data);
    }

    error_data.auid = this.get_visitor_id();

    this.send_event({
      event_timestamp_ms: this.get_timestamp(),
      tab_id: this.tab_id,
      event_type: "ecommerce_error",
      payload: error_data
    }).then((response) => {
      if (response == "OK") {
        this.log("NativeAnalytics Error Logged success");
      } else {
        console.error("NativeAnalytics Error Logged failed");
      }
    });
  }

  api_perf_triggered(perf_data): void {
    if (!this.has_consent_to_run) {
      return;
    } else {
      console.log("Native Analytics Ecommerce Perf Data Triggered");
    }

    this.send_event({
      event_timestamp_ms: this.get_timestamp(),
      tab_id: this.tab_id,
      event_type: "ecommerce_api_perf",
      payload: perf_data
    }).then((response) => {
      if (response == "OK") {
        this.log("Native Analytics Ecommerce Perf Data Logged success");
      } else {
        console.error("Native Analytics Ecommerce Perf Data Logged failed");
      }
    });
  }
}
